import { endStroke, END_SEQUENCE } from '../hero/JudgementEndSequence.js';
import { Color, Group, PointLight, Vector3 } from 'three';
import { BladeImpact } from '../../vfx/BladeImpact.js';
import { DustBurst } from '../../vfx/DustBurst.js';
import { ShockRing } from '../../vfx/ShockRing.js';
import { settings } from '../../config/settings.js';
import { EffectAtlas } from './EffectAtlas.js';
import { GlowPool } from './GlowPool.js';
import { SlashTrail } from './SlashTrail.js';
import { Shards } from './Shards.js';

const _p = new Vector3();
const _hand = new Vector3();

/**
 * The game's effects, in one place, built mostly out of the template's own:
 * the flight blades' impact shower for sparks, the judgement's shock ring and
 * dust for slams. Each pool shares its colours through uniforms, so a second
 * colour scheme is a second (cheap) instance rather than per-hit state.
 *
 * One point light is kept in the scene permanently at zero intensity and
 * flared on impacts — adding and removing lights would recompile every
 * material in the frame it happened.
 */
export class Effects {
  constructor(game) {
    this.game = game;
    this.group = new Group();
    this.group.name = 'GameFX';
    this.effectAtlas = new EffectAtlas(this.group);
    const terrain = game.app.terrain;

    this.trail = new SlashTrail();
    this.leftTrail = new SlashTrail();
    this.glow = new GlowPool(game.quality.name === 'low' ? 320 : 640);

    this.hitSparks = new BladeImpact(512);
    this.parrySparks = new BladeImpact(384);
    this.slamRing = new ShockRing({ terrain });
    this.dangerRing = new ShockRing({ terrain });
    this.coldRing = new ShockRing({ terrain });
    this.shards = new Shards(game.quality.name === 'low' ? 64 : 112);
    this.dustBurst = new DustBurst(game.quality.name === 'low' ? 512 : 1024);

    this.group.add(
      this.trail.mesh,
      this.leftTrail.mesh,
      this.glow.points,
      this.hitSparks.mesh,
      this.parrySparks.mesh,
      this.slamRing.mesh,
      this.dangerRing.mesh,
      this.coldRing.mesh,
      this.shards.mesh,
      this.dustBurst.mesh
    );

    this.light = new PointLight('#ffb070', 0, 9, 2);
    this.light.position.set(0, -50, 0);
    this.group.add(this.light);
    this._lightTime = 0;
    this._lightLife = 0;
    this._lightPeak = 0;

    const impact = settings.flight.blades.impact;
    this.hitConfig = { ...impact, color: '#ffd8a8', ringColor: '#ff6a1a', sparkColor: '#ff9a40', size: 0.75, sparks: 12, life: 0.22, intensity: 1.8, spikes: 5, sparkSpeed: 6, sparkStretch: 0.02, sparkSize: 0.035, sparkLife: 0.35 };
    this.heavyConfig = { ...this.hitConfig, size: 1.25, sparks: 24, life: 0.32, intensity: 2.6, spikes: 8, sparkSpeed: 9 };
    this.parryConfig = { ...impact, color: '#ffffff', ringColor: '#9fd8ff', sparkColor: '#ffe0a0', size: 1.9, sparks: 46, life: 0.45, intensity: 3.2, spikes: 9, spikeLength: 2.0, sparkSpeed: 11, sparkLife: 0.6, sparkStretch: 0.03, sparkSize: 0.04 };
    this.blockConfig = { ...this.parryConfig, size: 0.9, sparks: 24, life: 0.25, intensity: 2.2, spikes: 5 };
    const shock = settings.judgement.shock;
    this.slamConfig = { ...shock, color: '#ffb070', crackColor: '#ff6a20', radius: 3.3, life: 0.55, intensity: 2.2 };
    this.dangerConfig = { ...shock, color: '#ff3018', crackColor: '#ff2a10', radius: 3.6, life: 0.6, intensity: 2.6 };
    this.coldConfig = { ...shock, color: '#8fdcff', crackColor: '#d8f6ff', radius: 3.0, life: 0.55, intensity: 2.4 };
    this.dustConfig = { ...settings.judgement.dust, color: '#5d6470', shadeColor: '#1a1e26', soilColor: '#20242c', opacity: 0.6, puffs: 18, clods: 14 };

    this._afterimageAcc = 0;
    this._bladeBound = false;
  }

  /** Find the katana on the body, once it is equipped. */
  bindBlade() {
    const slot = this.game.app.characterScreen?.equipment.get('sword');
    if (!slot?.model) return false;
    const hand = this.game.app.character.getBone('RightHand');
    hand?.getWorldPosition(_hand);
    this.trail.bind(slot.model, _hand);
    this._bladeBound = true;
    return true;
  }

  /* ---- one-shots ---- */

  hit(point, dirX, dirZ, { heavy = false, color = null } = {}) {
    const config = heavy ? this.heavyConfig : this.hitConfig;
    this.hitSparks.burst(point.x, point.y, point.z, dirX, 0.15, dirZ, config, 1);
    this.glow.spawn(point, color ?? '#ff9a50', heavy ? 1.4 : 0.8, 0.14, { grow: 0.8, intensity: 2 });
    this.flare(point, heavy ? '#ffb070' : '#ff9050', heavy ? 26 : 12, 0.14);
  }

  parry(point, dirX, dirZ) {
    this.parrySparks.burst(point.x, point.y, point.z, dirX, 0.3, dirZ, this.parryConfig, 1);
    // Bright but brief and small: the body behind it stays readable.
    this.glow.spawn(point, '#ffffff', 1.1, 0.14, { grow: 1.2, star: true, intensity: 2.4 });
    this.glow.burst(point, '#ffe9b0', 16, { speed: 8, size: 0.05, life: 0.45, up: 2.2, gravity: -12 });
    this.flare(point, '#d8ecff', 30, 0.22);
  }

  block(point, dirX, dirZ) {
    this.parrySparks.burst(point.x, point.y, point.z, dirX, 0.2, dirZ, this.blockConfig, 1);
    this.flare(point, '#ffd8a0', 14, 0.12);
  }

  slam(position, radius, color = '#ffb070') {
    const danger = color === '#ff3a1a';
    const ring = danger ? this.dangerRing : this.slamRing;
    const config = danger ? this.dangerConfig : this.slamConfig;
    ring.burst(position.x, position.z, config, Math.max(0.3, radius / config.radius));
    this.dust(position, Math.min(2, radius / 2));
    _p.set(position.x, position.y + 0.3, position.z);
    this.flare(_p, color, 30, 0.3);
  }

  /** A blue-white ring on the ground — thunder strikes and ice. */
  coldBurst(position, radius) {
    this.coldRing.burst(position.x, position.z, this.coldConfig, Math.max(0.3, radius / this.coldConfig.radius));
  }

  /**
   * A fireball's end, or the 爆雷 reaction at full size: a white-hot core, a
   * ring along the ground, embers thrown up and smoke rolling out.
   */
  explosion(point, radius = 2, color = '#ff7a2a') {
    this.glow.spawn(point, '#fff0c8', radius * 0.7, 0.14, { grow: 1.4, intensity: 1.8 });
    this.glow.spawn(point, color, radius * 1.6, 0.4, { grow: 0.8, intensity: 2 });
    this.glow.burst(point, '#ffb040', Math.round(10 + radius * 6), { speed: 4 + radius * 2, size: 0.09, life: 0.8, up: 3, gravity: -7 });
    this.glow.burst(point, color, Math.round(6 + radius * 3), { speed: 2 + radius, size: 0.35, life: 0.6, up: 2.2, gravity: 1 });
    _p.set(point.x, Math.max(0, point.y - 1), point.z);
    this.slamRing.burst(_p.x, _p.z, this.slamConfig, Math.max(0.3, radius / this.slamConfig.radius));
    this.dust(_p, Math.min(2, radius / 2));
    this.flare(point, color, 14 + radius * 7, 0.35);
  }

  /** Ice breaking: splinters, a cold ring, a pale flash. */
  shatter(point, radius = 1.2, count = 12) {
    this.shards.burst(point, count, 4 + radius * 2);
    this.glow.spawn(point, '#d8fbff', radius * 1.3, 0.2, { grow: 1, intensity: 2.4 });
    this.glow.burst(point, '#9fefff', 10, { speed: 4, size: 0.06, life: 0.5, up: 2, gravity: -8 });
    _p.set(point.x, Math.max(0, point.y - 1), point.z);
    this.coldBurst(_p, radius);
    this.flare(point, '#9fe8ff', 18 + radius * 6, 0.3);
  }

  /** A column of fire standing out of the ground (the boss's burning floor). */
  firePillar(position, height = 3) {
    for (let i = 0; i < 6; i++) {
      _p.set(position.x + (Math.random() - 0.5) * 0.5, position.y + (i / 6) * height, position.z + (Math.random() - 0.5) * 0.5);
      this.glow.spawn(_p, i % 2 ? '#ff5a1a' : '#ffb040', 0.9 - i * 0.08, 0.7, { vy: 3.5, grow: -0.4, intensity: 2 });
    }
  }

  dust(position, strength = 1) {
    this.dustBurst.burst(position.x, position.y + 0.05, position.z, this.dustConfig, strength);
  }

  /** The tell before a blow: a star on the weapon. Red means don't block. */
  telegraph(point, danger = false) {
    this.glow.spawn(point, danger ? '#ff2a14' : '#ffd890', danger ? 1.6 : 1.0, danger ? 0.55 : 0.4, {
      star: true,
      grow: 0.6,
      intensity: danger ? 3 : 2.2
    });
  }

  /** Dark mist off the body while it is dodging. */
  afterimage(character, dt) {
    this._afterimageAcc += dt;
    if (this._afterimageAcc < 0.03) return;
    this._afterimageAcc = 0;
    const p = character.position;
    for (let i = 0; i < 2; i++) {
      _p.set(p.x + (Math.random() - 0.5) * 0.4, p.y + 0.4 + Math.random() * 1.2, p.z + (Math.random() - 0.5) * 0.4);
      this.glow.spawn(_p, '#4a6aa8', 0.5, 0.35, { grow: 1.2, intensity: 0.5 });
    }
  }

  afterimageAt(enemy) {
    const p = enemy.position;
    for (let i = 0; i < 6; i++) {
      _p.set(p.x + (Math.random() - 0.5) * 0.5, p.y + 0.3 + Math.random() * 1.3, p.z + (Math.random() - 0.5) * 0.5);
      this.glow.spawn(_p, '#8a50ff', 0.6, 0.4, { grow: 1, intensity: 0.6 });
    }
  }

  /** Flare the shared light at a point. */
  flare(point, color, intensity, life) {
    if (intensity < this._lightPeak * (1 - this._lightTime / Math.max(1e-3, this._lightLife))) return;
    this.light.color.set(color);
    this.light.position.set(point.x, point.y + 0.4, point.z);
    this._lightPeak = intensity;
    this._lightLife = life;
    this._lightTime = 0;
  }

  update(dt, elapsed) {
    if (!this._bladeBound) this.bindBlade();
    const p=this.game.player,silent=p.state==='attack'&&p.move?.config?.silentIai,iai=!silent&&p.weapon.id==='katana'&&p.state==='attack'&&p.move===p.heavy&&!this.game.form.active;
    this.effectAtlas.update(dt);
    const hiddenBlade=p.weapon.id==='katana'&&(silent||!!p.techniques?.ritual||!!p.techniques?.endBurst);
    if(hiddenBlade){
      const sword=this.game.weapons.blade();
      if(sword){if(!this._hiddenSword)this._hiddenSword={node:sword,visible:sword.visible};sword.visible=false;}
      const copy=this.game.heroPresence?.katanaSheath?.copy;if(copy)copy.visible=false;
    }else if(this._hiddenSword){this._hiddenSword.node.visible=this._hiddenSword.visible;this._hiddenSword=null;}

    if(!silent){this._silentMove=null;this._silentClosed=false;}
    else {
      if(this._silentMove!==p.move){this._silentMove=p.move;this._silentClosed=false;}
      if(!this._silentClosed&&p.move.phase>=.62){
        this._silentClosed=true;
        const hand=p.character.getBone('RightHand');
        if(hand)this.effectAtlas.spawn('71330',hand.getWorldPosition(new Vector3()),.7,.22);
      }
    }
    const endCut=!p.techniques?.hidden&&p.techniques?.ritual?.end&&p.techniques.ritual.t>=END_SEQUENCE.travel[0]&&p.techniques.ritual.t<END_SEQUENCE.field;
    const sword=p.weapon.id==='katana'&&p.state==='attack'&&!this.game.form.active;
    const uniforms=this.trail.material.uniforms;
    uniforms.uColor.value.set(endCut?'#715fff':sword?'#769fff':'#ffb36a');
    uniforms.uCore.value.set(endCut?'#9fedff':sword?'#effbff':'#fff6e8');
    uniforms.uLife.value=endCut?.085:iai?.14:.11;
    uniforms.uBand.value=endCut?.55:sword?.82:0;
    if(iai){
      // Keep the crouched silhouette readable; light appears at the draw,
      // rather than washing out the held stance or the recovery.
      const phase=p.move.phase;
      this._iaiStrength=phase>.12&&phase<.82?1.05:0;
      if(!this._iaiDraw&&phase>=.12){
        this._iaiDraw=true;
        p.character.getBone('RightHand')?.getWorldPosition(_hand);
        this.glow.spawn(_hand,'#e6f9ff',.28,.09,{star:true,intensity:1.3,grow:.35});
        this.flare(_hand,'#a7d6ff',4,.09);
      }
      if(!this._iaiCut&&phase>=(p.move.config.hits?.[0]??.66)){
        this._iaiCut=true;
        p.character.getBone('RightHand')?.getWorldPosition(_hand);
        this.glow.burst(_hand,'#9bbdff',this.game.quality.name==='low'?4:7,{speed:1.8,size:.025,life:.18,up:.1,gravity:0});
      }
    }else{this._iaiDraw=false;this._iaiCut=false;}
    if(sword&&(p.move.config.referenceMotion||p.move.config.swordMotion)){const open=(p.move.config.hits??[]).some(hit=>p.move.phase>=Math.max(0,hit-.1)&&p.move.phase<=hit+.07);if(open&&!this.trail.active)this.trail.begin(.8);else if(!open)this.trail.end();}
    let endDrawing=false;
    if(endCut){const r=p.techniques.ritual,index=END_SEQUENCE.travel.findLastIndex(t=>r.t>=t),end=index<4?END_SEQUENCE.travel[index+1]:END_SEQUENCE.field-.1,phase=Math.max(0,Math.min(1,(r.t-END_SEQUENCE.travel[index])/(end-END_SEQUENCE.travel[index]))),at=p.character.getBone('Hips').getWorldPosition(p.character.position.clone());endDrawing=endStroke(phase).drawing;at.y+=.16;p.techniques.assets.travel(at,p.character.facing,phase);}else p.techniques?.assets.travel(null);
    if(endDrawing){if(!this._endCutTrail)this.trail.begin(.8);this._endCutTrail=true;}else if(this._endCutTrail){this.trail.end();this._endCutTrail=false;}
    if(silent){this.trail.end();this.leftTrail.end();}
    this.trail.update(dt);
    if(iai)uniforms.uStrength.value=this._iaiStrength;
    this.leftTrail.update(dt);
    this.glow.update(dt, this.game.app.renderer.size.height * this.game.app.renderer.gl.getPixelRatio());
    this.hitSparks.sync(elapsed, this.hitConfig);
    this.parrySparks.sync(elapsed, this.parryConfig);
    this.slamRing.update(dt, this.slamConfig);
    this.dangerRing.update(dt, this.dangerConfig);
    this.coldRing.update(dt, this.coldConfig);
    this.shards.update(dt);
    this.dustBurst.sync(elapsed, this.dustConfig);

    this._lightTime += dt;
    const t = this._lightLife > 0 ? Math.min(1, this._lightTime / this._lightLife) : 1;
    this.light.intensity = this._lightPeak * (1 - t) * (1 - t);
  }

  clear() {
    this.effectAtlas.clear();
    if(this._hiddenSword){this._hiddenSword.node.visible=this._hiddenSword.visible;this._hiddenSword=null;}
    this._silentMove=null;this._silentClosed=false;
    this.glow.clear();
    this.hitSparks.clear();
    this.parrySparks.clear();
    this.slamRing.clear();
    this.dangerRing.clear();
    this.coldRing.clear();
    this.shards.clear();
    this.dustBurst.clear();
    this.trail.end();
    this.leftTrail.end();
  }

  dispose() {
    this.effectAtlas.dispose();
    this.trail.dispose();
    this.leftTrail.dispose();
    this.glow.dispose();
    this.hitSparks.dispose();
    this.parrySparks.dispose();
    this.slamRing.dispose();
    this.dangerRing.dispose();
    this.coldRing.dispose();
    this.shards.dispose();
    this.dustBurst.dispose();
  }
}

export { Color };
